import React, { useEffect, useState } from 'react';
import Icon from '../components/Icon';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import './Analytics.css';

function StatCard({ icon, label, value, color, sub }) {
  return (
    <div className="an-stat" style={{ '--c': color }}>
      <div className="an-stat-icon">{icon}</div>
      <div className="an-stat-info">
        <div className="an-stat-value">{value}</div>
        <div className="an-stat-label">{label}</div>
        {sub && <div className="an-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function BarRow({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="an-bar-row">
      <div className="an-bar-label">{label}</div>
      <div className="an-bar-track">
        <div className="an-bar-fill" style={{ width: pct + '%', background: color }} />
      </div>
      <div className="an-bar-value">{value}</div>
    </div>
  );
}

function MiniChart({ data, color }) {
  if (!data || !data.length) return null;
  const w = 280, h = 60;
  const max = Math.max(...data.map(d => d.v), 1);
  const step = w / Math.max(data.length - 1, 1);
  const pts = data.map((d, i) => `${i * step},${h - (d.v / max) * (h - 6) - 3}`).join(' ');
  const area = `0,${h} ${pts} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="an-spark" preserveAspectRatio="none">
      <polygon points={area} fill={color} opacity="0.12" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const STATUS_COLORS = {
  open: '#6b7280', assigned: '#7c3aed', in_progress: '#0369a1', pending: '#d97706',
  resolved: '#059669', closed: '#374151', cancelled: '#dc2626',
  approved: '#059669', completed: '#374151', delivered: '#059669', rejected: '#dc2626',
  submitted: '#d97706', manager_approved: '#7c3aed', hr_approved: '#0369a1',
  director_approved: '#059669', manager_rejected: '#dc2626', hr_rejected: '#dc2626', director_rejected: '#dc2626',
};
const ROLE_COLORS = {
  admin: '#7c3aed', department_head: '#007c82', staff: '#0369a1',
  teacher: '#059669', student: '#d97706', parent: '#dc2626',
  ibtidai_sinif_muellimi: '#10b981',
};

export default function Analytics() {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    API.get('/analytics')
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.message || e.message || 'Network error'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="an-loading"><div className="spinner"></div><span>{t('common.loading')}</span></div>;
  if (error) return <div className="an-error"><Icon name="warning" size={15} /> {error}</div>;
  if (!data) return null;

  const ov = data.overview || {};
  const sla = data.slaStats || {};
  const proc = data.procTotal || {};
  const slaPct = sla.total > 0 ? Math.round(100 * (1 - (sla.breached / sla.total))) : 100;

  return (
    <div className="an-page">
      <PageHeader icon="grades" title={t('analytics.title')} subtitle={t('analytics.subtitle')} />

      {/* Overview cards */}
      <div className="an-stats-grid">
        <StatCard icon={<Icon name="graduation" size={22} />} label={t('analytics.students')}    value={ov.students || 0}   color="#007c82" />
        <StatCard icon={<Icon name="teacher" size={22} />} label={t('analytics.teachers')}  value={ov.teachers || 0}   color="#3498db" />
        <StatCard icon={<Icon name="school" size={22} />} label={t('analytics.classes')}     value={ov.classes || 0}    color="#7c3aed" />
        <StatCard icon={<Icon name="book" size={22} />} label={t('analytics.subjects')}    value={ov.subjects || 0}   color="#059669" />
        <StatCard icon={<Icon name="users" size={22} />} label={t('analytics.totalUsers')}  value={ov.totalUsers || 0} color="#d97706" />
        <StatCard icon={<Icon name="target" size={22} />} label={t('analytics.slaCompliance')} value={slaPct + '%'}     color={slaPct >= 80 ? '#059669' : slaPct >= 50 ? '#d97706' : '#dc2626'} sub={(sla.breached || 0) + ' ' + t('analytics.breached')} />
      </div>

      <div className="an-grid">
        {/* Users by role */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="users" size={16} /> {t('analytics.usersByRole')}</h3>
          {(() => {
            const list = data.usersByRole || [];
            const max = Math.max(...list.map(x => x.count), 1);
            return list.map(r => (
              <BarRow key={r.role} label={t('roles.' + r.role, r.role)} value={r.count} max={max} color={ROLE_COLORS[r.role] || '#64748b'} />
            ));
          })()}
        </div>

        {/* Enrollment by section */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="briefcase" size={16} /> {t('analytics.enrollmentBySection')}</h3>
          {(data.enrollmentBySection || []).map(e => {
            const lbl = e.section === 'preschool' ? t('classes.sectionPreschool')
              : e.section === 'primary' ? t('classes.sectionPrimary')
              : e.section === 'secondary' ? t('classes.sectionSecondary') : e.section;
            return (
              <div key={e.section} className="an-section-row">
                <div>
                  <div className="an-section-label">{lbl}</div>
                  <div className="an-section-meta">
                    {t('analytics.active')}: <b>{e.active || 0}</b> · {t('analytics.signed')}: <b style={{color:'#059669'}}>{e.signed || 0}</b>
                  </div>
                </div>
                <div className="an-section-num">{e.count || 0}</div>
              </div>
            );
          })}
        </div>

        {/* Requests by status */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="clipboard" size={16} /> {t('analytics.requestsByStatus')}</h3>
          {(() => {
            const list = data.requestsByStatus || [];
            const max = Math.max(...list.map(x => x.count), 1);
            return list.map(r => (
              <BarRow key={r.status} label={t('status.' + r.status, r.status)} value={r.count} max={max} color={STATUS_COLORS[r.status] || '#64748b'} />
            ));
          })()}
        </div>

        {/* Requests by department */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="building" size={16} /> {t('analytics.requestsByDept')}</h3>
          {(() => {
            const list = data.requestsByDept || [];
            const max = Math.max(...list.map(x => x.total), 1);
            return list.map(d => {
              const deptLbl = lang === 'en' ? t('departments.' + d.department, d.department) : d.department;
              return (
                <div key={d.department} className="an-dept-row">
                  <div className="an-dept-name">{deptLbl}</div>
                  <div className="an-dept-bar">
                    <div className="an-dept-fill" style={{ width: ((d.total / max) * 100) + '%' }} />
                  </div>
                  <div className="an-dept-stats">
                    <span>{t('analytics.total')}: <b>{d.total}</b></span>
                    <span>{t('analytics.resolved')}: <b style={{color:'#059669'}}>{d.resolved || 0}</b></span>
                    {d.avg_time != null && <span>{t('analytics.avg')}: <b>{d.avg_time}m</b></span>}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Monthly requests trend */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="chartLine" size={16} /> {t('analytics.requestsMonthly')}</h3>
          <MiniChart data={(data.requestsMonthly || []).map(m => ({ v: m.total }))} color="#0369a1" />
          <div className="an-trend-rows">
            {(data.requestsMonthly || []).map(m => (
              <div key={m.month} className="an-trend-row">
                <span className="an-trend-month">{m.month}</span>
                <span>{t('analytics.total')}: <b>{m.total}</b></span>
                <span style={{color:'#059669'}}>{t('analytics.resolved')}: <b>{m.resolved || 0}</b></span>
              </div>
            ))}
          </div>
        </div>

        {/* Top performers */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="trophy" size={16} /> {t('analytics.topPerformers')}</h3>
          {(data.topPerformers || []).map((p, i) => (
            <div key={i} className="an-perf-row">
              <span className="an-perf-rank">{i < 3 ? <Icon name="medal" size={18} style={{color: ['#d4a017','#94a3b8','#b45309'][i]}} /> : (i + 1)}</span>
              <div className="an-perf-info">
                <div className="an-perf-name">{p.name}</div>
                <div className="an-perf-pos">{lang === 'en' ? t('positions.' + p.position, p.position || '') : (p.position || '')}</div>
              </div>
              <div className="an-perf-stats">
                <div><b>{p.resolved}</b> {t('analytics.solved')}</div>
                {p.avg_rating != null && <div style={{color:'#f59e0b'}}><Icon name="star" size={12} filled /> {p.avg_rating}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* Procurement */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="package" size={16} /> {t('analytics.procurement')}</h3>
          <div className="an-section-row">
            <div className="an-section-label">{t('analytics.total')}</div>
            <div className="an-section-num" style={{color:'#0f172a'}}>{proc.count || 0}</div>
          </div>
          <div className="an-section-row">
            <div className="an-section-label">{t('status.completed')}</div>
            <div className="an-section-num" style={{color:'#059669'}}>{proc.completed || 0}</div>
          </div>
          {(() => {
            const list = data.procByStatus || [];
            const max = Math.max(...list.map(x => x.count), 1);
            return list.map(r => (
              <BarRow key={r.status} label={t('status.' + r.status, r.status)} value={r.count} max={max} color={STATUS_COLORS[r.status] || '#64748b'} />
            ));
          })()}
        </div>

        {/* Leave */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="sun" size={16} /> {t('analytics.leave')}</h3>
          {(() => {
            const list = data.leaveByStatus || [];
            const max = Math.max(...list.map(x => x.count), 1);
            return list.map(r => (
              <BarRow key={r.status} label={t('status.' + r.status, r.status)} value={r.count} max={max} color={STATUS_COLORS[r.status] || '#64748b'} />
            ));
          })()}
          <div className="an-trend-rows" style={{marginTop:14}}>
            {(data.leaveByType || []).map(lt => {
              const lbl = lt.absence_type === 'medical' ? t('leave.medical')
                : lt.absence_type === 'family' ? t('leave.family')
                : lt.absence_type === 'education' ? t('leave.education')
                : lt.absence_type === 'personal' ? t('leave.personal')
                : lt.absence_type === 'other' ? t('leave.other') : lt.absence_type;
              return (
                <div key={lt.absence_type} className="an-trend-row">
                  <span className="an-trend-month">{lbl}</span>
                  <span><b>{lt.count}</b></span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grades distribution */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="chartBar" size={16} /> {t('analytics.gradesDist')}</h3>
          {(() => {
            const list = data.gradesDist || [];
            const max = Math.max(...list.map(x => x.count), 1);
            const colors = ['#059669','#10b981','#0891b2','#f59e0b','#dc2626'];
            return list.map((g, i) => (
              <BarRow key={g.band} label={g.band} value={g.count} max={max} color={colors[i % colors.length]} />
            ));
          })()}
        </div>

        {/* Grades by subject */}
        <div className="an-card">
          <h3 className="an-card-title"><Icon name="book" size={16} /> {t('analytics.gradesBySubject')}</h3>
          {(data.gradesBySubject || []).map(s => {
            const subjLbl = lang === 'en' ? t('subjectNames.' + s.subject, s.subject) : s.subject;
            const color = s.avg_grade >= 80 ? '#059669' : s.avg_grade >= 60 ? '#0891b2' : s.avg_grade >= 40 ? '#f59e0b' : '#dc2626';
            return (
              <div key={s.subject} className="an-subj-row">
                <div className="an-subj-name">{subjLbl}</div>
                <div className="an-subj-avg" style={{color}}>{s.avg_grade ?? '—'}</div>
                <div className="an-subj-count">{s.count}</div>
              </div>
            );
          })}
        </div>

        {/* Attendance daily */}
        <div className="an-card an-card-wide">
          <h3 className="an-card-title"><Icon name="clipboard" size={16} /> {t('analytics.attendance30')}</h3>
          <MiniChart data={(data.attendanceDaily || []).map(d => ({ v: d.present || 0 }))} color="#059669" />
          <div style={{display:'flex',gap:14,fontSize:'0.86rem',marginTop:10,color:'#475569'}}>
            <span>● {t('analytics.present')}: <b style={{color:'#059669'}}>{(data.attendanceDaily || []).reduce((s, d) => s + (+d.present || 0), 0)}</b></span>
            <span>● {t('analytics.absent')}: <b style={{color:'#dc2626'}}>{(data.attendanceDaily || []).reduce((s, d) => s + (+d.absent || 0), 0)}</b></span>
            <span>● {t('analytics.late')}: <b style={{color:'#f59e0b'}}>{(data.attendanceDaily || []).reduce((s, d) => s + (+d.late || 0), 0)}</b></span>
          </div>
        </div>
      </div>
    </div>
  );
}
